import { Component, OnDestroy, OnInit, HostBinding } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { PageEvent } from '@angular/material/paginator';
import { NavigationEnd, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { EmissionService } from 'src/app/shared/service/emission-service/emission.service';
import { debounceTime, distinctUntilChanged, filter, Subject, takeUntil, forkJoin, of } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import { enrichRowWithMergedOnboardingModules } from 'src/app/shared/helpers/onboarding-modules.util';
import {
  applyOnboardingSectionSave,
  buildCargoHeatingDetailApiConfig,
  buildCargoHeatingDetailSections,
  buildEmissionDetailApiConfig,
  buildEmissionDetailSections,
  formatLastUpdated,
  formatOnboardingStatus,
  mapEmissionApiRow,
  normalizeDetailRow,
  mapQuickOnboardingToDetailRow,
  mergeCargoHeatingDetailSources,
  normalizeImoDigits,
  normalizePortOnboardingDetailRows,
  normalizeEmissionDetailRow,
  normalizeEmissionDetailRowForImo,
  normalizeVesselList,
  OnboardingDetailSection,
  OnboardingSectionSavePayload,
  OnboardingViewMode,
  pickField,
  pickImo,
  pickVesselName,
  sectionHasMeaningfulContent,
} from './onboarding-vessel-mapper.util';

@Component({
  selector: 'app-onboarding-vessels',
  templateUrl: './onboarding-vessels.component.html',
  styleUrls: ['./onboarding-vessels.component.scss'],
})
export class OnboardingVesselsComponent implements OnInit, OnDestroy {
  viewMode: OnboardingViewMode = 'list';
  RetriveData: any[] = [];
  paginatedData: any[] = [];
  vesselImoList: any[] = [];
  itemsPerPage: number = 100;
  currentPage: number = 0;
  columnKeys: string[] = [];
  vesselName: any;
  isExpanded: boolean = false;
  filterForm: any;
  filteredData: any[] = [];
  elements: any[] = [];
  selectedShips: any;
  clientName: any;
  vesselNameSearchText = '';
  filteredVesselSuggestions: Array<{ vessel_name: string; vessel_imo: string | number }> = [];
  isFilterOpen = false;
  private readonly vesselNameSearch$ = new Subject<string>();

  cpLimits: any = null;
  latestMainConfig: any = null;
  isEditingLimits: boolean = false;
  cpLimitsForm: any;
  selectedImo: any = null;
  activeLogEditCardIndex: number = -1;
  newSpeedVal: string = '';
  newConsVal: string = '';
  newSpeedError: string = '';
  localSpeedCons: any = {};
  localSpeedConsArray: Array<{ speed: string; cons: string }> = [];

  cargoHeatingVessels: Record<string, unknown>[] = [];
  emissionVessels: Record<string, unknown>[] = [];
  cargoHeatingDetail: Record<string, unknown> | null = null;
  emissionDetail: Record<string, unknown> | null = null;
  cargoHeatingDetailSections: OnboardingDetailSection[] = [];
  emissionDetailSections: OnboardingDetailSection[] = [];
  selectedCargoHeatingVessel: Record<string, unknown> | null = null;
  selectedEmissionVessel: Record<string, unknown> | null = null;
  isLoadingChDetail = false;
  isLoadingEmissionDetail = false;
  isLoadingPortDetail = false;
  savingCargoSectionId: string | null = null;
  savingEmissionSectionId: string | null = null;
  private cargoHeatingDetailSeq = 0;
  private emissionDetailSeq = 0;
  private portDetailRequestSeq = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private requestFormService: RequestFormService,
    private pRequestFormService: PortRequestFormServiceService,
    private chService: CargoheatingCommonService,
    private emissionService: EmissionService,
    private router: Router,
    private fb: FormBuilder,
    private toastr: ToastrService,
  ) {
    this.filterForm = this.fb.group({
          from_date: ['', [Validators.required]],
          to_date: ['', [Validators.required]],
          overConsumption: [''],
          vesselName: [''],
          client: [''],
          imoNumber: [''],
        });

    this.cpLimitsForm = this.fb.group({
      wind_bf: [4, [Validators.required]],
      sig_wave: [1.25, [Validators.required]],
      adverse_current: ['Excluded', [Validators.required]],
      swell_height: [2.0, [Validators.required]],
      wind_wave: [1.25, [Validators.required]],
      about_sc: ['Yes', [Validators.required]],
      current_factor: ['No', [Validators.required]],
      bad_weather_hours: [12, [Validators.required]]
    });
  }
  ngOnInit(): void {
    this.loadDummyData();
    this.generateUniqueVesselImoList();
    this.loadModuleOnboardedVessels();
    this.paginatedData = [];

    this.vesselNameSearch$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.applyVesselNameSuggestions(term));

    this.emissionService.emissionVesselsRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadEmissionOnboardedVessels());

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.router.url.includes('pc-OnboardingVessels')) {
          this.loadModuleOnboardedVessels();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isListView(): boolean {
    return this.viewMode === 'list';
  }

  get isPortDetailView(): boolean {
    return this.viewMode === 'port';
  }

  get isCargoHeatingDetailView(): boolean {
    return this.viewMode === 'cargo_heating';
  }

  get isEmissionDetailView(): boolean {
    return this.viewMode === 'emission';
  }

  get pageTitle(): string {
    if (this.isPortDetailView) {
      return this.vesselName
        ? `${this.vesselName} — Onboarding Details`
        : 'Vessel Onboarding Details';
    }
    if (this.isCargoHeatingDetailView) {
      return 'Cargo Heating Onboarding Details';
    }
    if (this.isEmissionDetailView) {
      return 'Emission Onboarding Details';
    }
    return 'View Onboarded Vessels';
  }

  get paginatedVesselList(): any[] {
    const start = this.currentPage * this.itemsPerPage;
    return this.filteredData.slice(start, start + this.itemsPerPage);
  }

  get showNoVesselSuggestions(): boolean {
    return (
      !!this.vesselNameSearchText.trim() &&
      this.filteredVesselSuggestions.length === 0
    );
  }

  /** True when this vessel has cargo-heating onboarding (list match or real CH fields). */
  get hasCargoHeatingOnboarding(): boolean {
    if (!this.selectedImo) {
      return false;
    }
    if (this.visibleCargoHeatingVessels.length > 0) {
      return true;
    }
    const ch = this.cargoHeatingDetail;
    if (
      ch &&
      (ch['main_boiler'] ||
        ch['heating_table'] ||
        ch['pipeline_configuration'] ||
        ch['capacity_plan'] ||
        ch['boiler_efficiency_curve'] ||
        ch['composite_boiler'] ||
        ch['egb_economizer'] ||
        ch['heating_setup'] ||
        ch['tank_count'])
    ) {
      return true;
    }
    return (this.cargoHeatingDetailSections ?? []).some(sectionHasMeaningfulContent);
  }

  /** Embed CH cards on port vessel detail only for cargo-heating onboarded vessels. */
  get showModuleVesselSections(): boolean {
    return this.isPortDetailView && this.hasCargoHeatingOnboarding;
  }

  get visibleCargoHeatingVessels(): Record<string, unknown>[] {
    if (!this.isPortDetailView || !this.selectedImo) {
      return [];
    }
    return this.cargoHeatingVessels.filter((vessel) =>
      this.imoMatches(this.selectedImo, vessel),
    );
  }

  get visibleEmissionVessels(): Record<string, unknown>[] {
    if (!this.isPortDetailView || !this.selectedImo) {
      return [];
    }
    return this.emissionVessels.filter((vessel) =>
      this.imoMatches(this.selectedImo, vessel),
    );
  }

  private imoMatches(
    selectedImo: unknown,
    vessel: Record<string, unknown>,
  ): boolean {
    if (!selectedImo) {
      return false;
    }
    const selected = normalizeImoDigits(selectedImo);
    const vesselImo = normalizeImoDigits(pickImo(vessel));
    return !!selected && !!vesselImo && selected === vesselImo;
  }

  private buildCargoHeatingFallback(
    imo: string,
    vesselName?: string,
  ): Record<string, unknown> {
    const normalizedImo = normalizeImoDigits(imo);
    const name = String(vesselName ?? this.vesselName ?? '').trim();
    return {
      vessel_name: name,
      imo: normalizedImo,
      vessel_imo: normalizedImo,
    };
  }

  private detailMatchesSelectedImo(
    detail: Record<string, unknown> | null | undefined,
  ): boolean {
    if (!detail || !this.selectedImo) {
      return false;
    }
    const detailImo = normalizeImoDigits(pickImo(detail));
    const selected = normalizeImoDigits(this.selectedImo);
    return !!detailImo && !!selected && detailImo === selected;
  }

  private buildEmissionFallback(imo: string): Record<string, unknown> {
    const normalizedImo = normalizeImoDigits(imo);
    const listMatch = this.emissionVessels.find((vessel) =>
      this.imoMatches(normalizedImo, vessel),
    );
    return {
      vessel_name: String(this.vesselName || '').trim(),
      imo: normalizedImo,
      vessel_imo: normalizedImo,
      ...(listMatch ?? {}),
    };
  }

  private applySelectedVesselIdentity(
    detail: Record<string, unknown>,
  ): Record<string, unknown> {
    const normalizedImo = normalizeImoDigits(this.selectedImo);
    if (!normalizedImo) {
      return detail;
    }

    const next = { ...detail };
    next['imo'] = normalizedImo;
    next['vessel_imo'] = normalizedImo;
    if (this.vesselName) {
      next['vessel_name'] = this.vesselName;
    }
    return next;
  }

  private clearVesselDetailState(): void {
    this.cargoHeatingDetailSeq += 1;
    this.emissionDetailSeq += 1;
    this.portDetailRequestSeq += 1;
    this.cargoHeatingDetail = null;
    this.emissionDetail = null;
    this.cargoHeatingDetailSections = [];
    this.emissionDetailSections = [];
    this.isLoadingChDetail = false;
    this.isLoadingEmissionDetail = false;
    this.isLoadingPortDetail = false;
    this.paginatedData = [];
    this.latestMainConfig = null;
    this.cpLimits = null;
    this.vesselTcpSpeedConsumption = [];
    this.savingCargoSectionId = null;
    this.savingEmissionSectionId = null;
  }

  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }

  @HostBinding('class.pc-light') get pcLight(): boolean {
    return !this.isDarkMode();
  }
  loadDummyData() {
    this.RetriveData;
  }
  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    // this.paginateData();
  }
  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }

generateUniqueVesselImoList() {
  this.pRequestFormService.getOnboardingVesselsData().subscribe((data) => {
    this.vesselImoList = data;
    this.elements = data;
    this.filteredData = data;
  });
}

onVesselNameInput(event: Event): void {
  const term = ((event.target as HTMLInputElement).value || '').toString();
  this.vesselNameSearchText = term;
  this.vesselNameSearch$.next(term);
}

onVesselNameSelected(event: MatAutocompleteSelectedEvent): void {
  const vesselName = String(event.option.value || '').trim();
  if (vesselName) {
    this.filterForm.patchValue({ vesselName });
    this.vesselNameSearchText = vesselName;
  }
}

private applyVesselNameSuggestions(term: string): void {
  const normalized = term.trim().toLowerCase();
  if (!normalized) {
    this.filteredVesselSuggestions = [];
    return;
  }

  this.filteredVesselSuggestions = (this.elements || [])
    .filter((item: { vessel_name?: string }) =>
      String(item?.vessel_name || '')
        .toLowerCase()
        .includes(normalized),
    )
    .slice(0, 20);
}

loadModuleOnboardedVessels(): void {
  this.chService.getCargoVesselOnboardingVessels().subscribe({
    next: (data) => {
      this.cargoHeatingVessels = normalizeVesselList(data);
    },
    error: () => {
      this.cargoHeatingVessels = [];
    },
  });

  this.loadEmissionOnboardedVessels();
}

private loadEmissionOnboardedVessels(): void {
  this.emissionService.getEmissionVesselOnboardingVessels().subscribe({
    next: (data) => {
      this.emissionVessels = data;
    },
    error: () => {
      // Keep existing list on API failure.
    },
  });
}


filterClick() {
  const imoNumber = this.filterForm.value.imoNumber?.trim();
  const vesselName = this.filterForm.value.vesselName?.trim()?.toLowerCase();

  if (!this.elements || this.elements.length === 0) {
    this.toastr.warning('No data available to filter.');
    return;
  }

  this.filteredData = this.elements.filter((item: any) => {
    const imoMatch =
      !imoNumber || item.vessel_imo?.toString() === imoNumber.toString();
    const itemName = item.vessel_name?.toLowerCase()?.trim();
    const nameMatch = !vesselName || itemName?.includes(vesselName);
    return imoMatch && nameMatch;
  });

  this.currentPage = 0;

  if (this.filteredData.length === 0) {
    this.toastr.info('No matching records found.');
  }

  this.isFilterOpen = false;
}

closeFilterDrawer(): void {
  this.isFilterOpen = false;
}

clearClick() {
  this.filterForm.patchValue({ imoNumber: '', vesselName: '' });
  this.vesselNameSearchText = '';
  this.filteredVesselSuggestions = [];
  this.filteredData = this.elements;
  this.currentPage = 0;
  this.isFilterOpen = false;
}


  count(filteredData: any) {
    throw new Error('Method not implemented.');
  }
  paginateData() {
    throw new Error('Method not implemented.');
  }
  createTcpChart() {
    throw new Error('Method not implemented.');
  }
  createChart() {
    throw new Error('Method not implemented.');
  }
  vesselTcpSpeedConsumption: any[] = [];

  filterByVesselAndImo(vessel: string, imo: string) {
    const normalizedImo = normalizeImoDigits(imo);
    if (!normalizedImo) {
      this.toastr.warning('Invalid IMO number for the selected vessel.');
      return;
    }

    this.clearVesselDetailState();
    this.viewMode = 'port';
    this.vesselName = vessel;
    this.selectedImo = normalizedImo;
    this.isEditingLimits = false;
    this.activeLogEditCardIndex = -1;
    this.isLoadingChDetail = true;

    this.loadPortOnboardingDetailForImo(normalizedImo, vessel);
    this.loadSpeedConsumptionForImo(normalizedImo, vessel);
    this.loadCargoHeatingDetailForImo(normalizedImo, vessel);
  }

  private loadSpeedConsumptionForImo(
    normalizedImo: string,
    vessel: string,
  ): void {
    const numericImo = Number(normalizedImo);

    forkJoin({
      tcpData: this.requestFormService
        .getVesselTcpSpeedConsumption(normalizedImo)
        .pipe(catchError(() => of([]))),
      mainConfig:
        Number.isFinite(numericImo) && numericImo > 0
          ? this.requestFormService
              .getOnboardingVesselDetails(numericImo)
              .pipe(catchError(() => of(null)))
          : of(null),
    }).subscribe({
      next: ({ tcpData, mainConfig }) => {
        if (this.selectedImo !== normalizedImo) {
          return;
        }

        if (mainConfig) {
          this.latestMainConfig = mainConfig;
        }

        let resolvedClientName = this.clientName || '';
        if (mainConfig) {
          const source =
            (mainConfig as any)?.onboarding_data ||
            (mainConfig as any)?.data ||
            mainConfig;
          const onboardingForm = source?.onboarding_form || {};
          const vesselObj = source?.vessel || {};
          resolvedClientName =
            onboardingForm.clientName ||
            vesselObj.client_name ||
            source.clientName ||
            resolvedClientName;
        }

        const validTcpData =
          Array.isArray(tcpData) &&
          tcpData.length > 0 &&
          tcpData.some(
            (item) =>
              item?.speed_consumption &&
              Object.keys(item.speed_consumption).length > 0,
          );

        if (validTcpData) {
          this.vesselTcpSpeedConsumption = tcpData;
          if (resolvedClientName) {
            this.clientName = resolvedClientName;
            this.vesselTcpSpeedConsumption.forEach((item) => {
              if (!item.client || item.client === 'N/A') {
                item.client = resolvedClientName;
              }
            });
          } else {
            this.clientName = this.vesselTcpSpeedConsumption[0]?.client || '';
          }
        } else if (mainConfig) {
          this.vesselTcpSpeedConsumption =
            this.extractSpeedConsumptionFromConfig(
              mainConfig,
              normalizedImo,
              vessel,
            );
          if (resolvedClientName) {
            this.clientName = resolvedClientName;
          }
        } else {
          this.vesselTcpSpeedConsumption = [];
        }

        this.fetchCpLimits(normalizedImo, this.clientName || '', mainConfig);
      },
      error: () => {
        if (this.selectedImo !== normalizedImo) {
          return;
        }
        this.vesselTcpSpeedConsumption = [];
        this.fetchCpLimits(normalizedImo, '');
      },
    });
  }

  private extractSpeedConsumptionFromConfig(
    mainConfig: any,
    imo: string,
    vesselName?: string,
  ): any[] {
    if (!mainConfig) return [];
    const source =
      (mainConfig as any)?.onboarding_data ||
      (mainConfig as any)?.data ||
      mainConfig;
    const onboardingForm = source?.onboarding_form || {};
    const vesselObj = source?.vessel || {};
    const clientName =
      onboardingForm.clientName ||
      vesselObj.client_name ||
      source.clientName ||
      this.clientName ||
      '';
    const shipName =
      onboardingForm.ship ||
      vesselObj.vessel_name ||
      vesselName ||
      this.vesselName ||
      '';
    const segment =
      onboardingForm.segment || vesselObj.segment || source.segment || '';
    const yearBuilt = onboardingForm.yearBuilt || source.yearBuilt || '';
    const shipBuilder = onboardingForm.shipYard || source.shipYard || '';

    const ballastRows: any[] = Array.isArray(onboardingForm.ballastRows)
      ? onboardingForm.ballastRows
      : [];
    const ladenRows: any[] = Array.isArray(onboardingForm.ladenRows)
      ? onboardingForm.ladenRows
      : [];

    const ballastMap: Record<string, string> = {};
    for (const row of ballastRows) {
      if (row && row.speed != null && row.consumption != null) {
        const s = row.speed.toString().trim();
        const c = row.consumption.toString().trim();
        if (s && c) {
          ballastMap[s] = c;
        }
      }
    }

    const ladenMap: Record<string, string> = {};
    for (const row of ladenRows) {
      if (row && row.speed != null && row.consumption != null) {
        const s = row.speed.toString().trim();
        const c = row.consumption.toString().trim();
        if (s && c) {
          ladenMap[s] = c;
        }
      }
    }

    const items: any[] = [];
    if (Object.keys(ballastMap).length > 0) {
      items.push({
        imo_number: imo,
        ship_name: shipName,
        condition: 'Ballast',
        client: clientName,
        segment: segment,
        speed_consumption: ballastMap,
        year_built: yearBuilt,
        ship_builder: shipBuilder,
      });
    }
    if (Object.keys(ladenMap).length > 0) {
      items.push({
        imo_number: imo,
        ship_name: shipName,
        condition: 'Laden',
        client: clientName,
        segment: segment,
        speed_consumption: ladenMap,
        year_built: yearBuilt,
        ship_builder: shipBuilder,
      });
    }

    return items;
  }

  private loadPortOnboardingDetailForImo(
    imo: string,
    vesselName: string,
  ): void {
    const normalizedImo = normalizeImoDigits(imo);
    if (!normalizedImo) {
      return;
    }

    const requestSeq = ++this.portDetailRequestSeq;
    this.isLoadingPortDetail = true;
    this.paginatedData = [];

    this.pRequestFormService
      .getOnboardingVesselsDetailData(normalizedImo)
      .subscribe({
        next: (data) => {
          if (
            requestSeq !== this.portDetailRequestSeq ||
            this.selectedImo !== normalizedImo
          ) {
            return;
          }

          const rows = normalizePortOnboardingDetailRows(
            data,
            normalizedImo,
            vesselName,
          );
          if (rows.length) {
            this.paginatedData = rows;
            this.isLoadingPortDetail = false;
            return;
          }

          this.loadQuickOnboardingDetailFallback(
            normalizedImo,
            vesselName,
            requestSeq,
          );
        },
        error: () => {
          if (
            requestSeq !== this.portDetailRequestSeq ||
            this.selectedImo !== normalizedImo
          ) {
            return;
          }
          this.loadQuickOnboardingDetailFallback(
            normalizedImo,
            vesselName,
            requestSeq,
          );
        },
      });
  }

  private loadQuickOnboardingDetailFallback(
    normalizedImo: string,
    vesselName: string,
    requestSeq: number,
  ): void {
    this.pRequestFormService
      .quickOnboardingByImo(Number(normalizedImo))
      .subscribe({
        next: (quickRes) => {
          if (
            requestSeq !== this.portDetailRequestSeq ||
            this.selectedImo !== normalizedImo
          ) {
            return;
          }
          const fallbackRow = mapQuickOnboardingToDetailRow(
            quickRes,
            normalizedImo,
            vesselName,
          );
          this.paginatedData = fallbackRow ? [fallbackRow] : [];
          this.isLoadingPortDetail = false;
        },
        error: () => {
          if (
            requestSeq !== this.portDetailRequestSeq ||
            this.selectedImo !== normalizedImo
          ) {
            return;
          }
          this.paginatedData = [];
          this.isLoadingPortDetail = false;
        },
      });
  }

  private applyMergedOnboardingModules(
    imo: string,
    detail: Record<string, unknown>,
    additionalSources: unknown[] = [],
    ensureModules: string[] = [],
  ): Record<string, unknown> {
    return enrichRowWithMergedOnboardingModules(imo, detail, {
      cargoHeatingVessels: this.cargoHeatingVessels,
      emissionVessels: this.emissionVessels,
      additionalSources,
      ensureModules,
    });
  }

  private loadCargoHeatingDetailForImo(imo: string, vesselName?: string): void {
    const normalizedImo = normalizeImoDigits(imo);
    if (!normalizedImo) {
      return;
    }

    const requestSeq = ++this.cargoHeatingDetailSeq;
    this.isLoadingChDetail = true;
    this.cargoHeatingDetail = null;
    this.cargoHeatingDetailSections = [];

    const fallback = this.buildCargoHeatingFallback(normalizedImo, vesselName);
    const numericImo = Number(normalizedImo);

    forkJoin({
      cargo: this.chService.getCargoHeatingOnboardingData(normalizedImo).pipe(
        catchError(() => of(null)),
      ),
      mainConfig:
        Number.isFinite(numericImo) && numericImo > 0
          ? this.requestFormService
              .getOnboardingVesselDetails(numericImo)
              .pipe(catchError(() => of(null)))
          : of(null),
    }).subscribe({
      next: ({ cargo, mainConfig }) => {
        if (
          requestSeq !== this.cargoHeatingDetailSeq ||
          this.selectedImo !== normalizedImo
        ) {
          return;
        }

        // Override legacy TCP client name with the actual onboarded client name and ensure speed consumption is populated
        if (mainConfig) {
          this.latestMainConfig = mainConfig;
          const source =
            (mainConfig as any)?.onboarding_data ||
            (mainConfig as any)?.data ||
            mainConfig;
          const onboardingForm = source?.onboarding_form || {};
          const vesselObj = source?.vessel || {};
          const realClientName =
            onboardingForm.clientName ||
            vesselObj.client_name ||
            source.clientName;

          if (realClientName) {
            this.clientName = realClientName;
            if (this.vesselTcpSpeedConsumption?.length > 0) {
              this.vesselTcpSpeedConsumption.forEach((item) => {
                if (!item.client || item.client === 'N/A') {
                  item.client = realClientName;
                }
              });
            }
          }

          if (
            !this.vesselTcpSpeedConsumption ||
            this.vesselTcpSpeedConsumption.length === 0
          ) {
            this.vesselTcpSpeedConsumption =
              this.extractSpeedConsumptionFromConfig(
                mainConfig,
                normalizedImo,
                vesselName || this.vesselName,
              );
          }
          if (!this.cpLimits || !this.hasMeaningfulCpLimits(this.cpLimits)) {
            this.applyCpLimitsFromConfig(
              mainConfig,
              this.clientName || realClientName || '',
            );
          }
        }

        const listMatch = this.cargoHeatingVessels.find((vessel) =>
          this.imoMatches(normalizedImo, vessel),
        );

        let detail = mergeCargoHeatingDetailSources({
          expectedImo: normalizedImo,
          fallback,
          cargoApi: cargo,
          mainVesselDetails: mainConfig,
          listVessel: listMatch ?? null,
        });
        detail = this.applySelectedVesselIdentity(detail);
        if (!this.detailMatchesSelectedImo(detail)) {
          detail = this.applySelectedVesselIdentity(
            mergeCargoHeatingDetailSources({
              expectedImo: normalizedImo,
              fallback,
              listVessel: listMatch ?? null,
            }),
          );
        }
        detail = this.applyMergedOnboardingModules(normalizedImo, detail);

        this.cargoHeatingDetail = detail;
        this.cargoHeatingDetailSections = buildCargoHeatingDetailSections(detail);
        this.isLoadingChDetail = false;
      },
      error: () => {
        if (
          requestSeq !== this.cargoHeatingDetailSeq ||
          this.selectedImo !== normalizedImo
        ) {
          return;
        }
        const listMatch = this.cargoHeatingVessels.find((vessel) =>
          this.imoMatches(normalizedImo, vessel),
        );
        let detail = mergeCargoHeatingDetailSources({
          expectedImo: normalizedImo,
          fallback,
          listVessel: listMatch ?? null,
        });
        detail = this.applySelectedVesselIdentity(detail);
        detail = this.applyMergedOnboardingModules(normalizedImo, detail);
        this.cargoHeatingDetail = detail;
        this.cargoHeatingDetailSections = buildCargoHeatingDetailSections(detail);
        this.isLoadingChDetail = false;
      },
    });
  }

  private loadEmissionDetailForImo(imo: string): void {
    const normalizedImo = normalizeImoDigits(imo);
    if (!normalizedImo) {
      return;
    }

    const requestSeq = ++this.emissionDetailSeq;
    this.isLoadingEmissionDetail = true;
    this.emissionDetail = null;
    this.emissionDetailSections = [];

    const fallback = this.buildEmissionFallback(normalizedImo);

    this.emissionService.getEmissionVesselOnboardingDetails(normalizedImo).subscribe({
      next: (data) => {
        if (
          requestSeq !== this.emissionDetailSeq ||
          this.selectedImo !== normalizedImo
        ) {
          return;
        }
        this.emissionDetail = this.applySelectedVesselIdentity(
          normalizeEmissionDetailRowForImo(normalizedImo, data, fallback),
        );
        if (!this.detailMatchesSelectedImo(this.emissionDetail)) {
          this.emissionDetail = this.applySelectedVesselIdentity(fallback);
        }
        this.emissionDetailSections = buildEmissionDetailSections(
          this.emissionDetail,
        );
        this.isLoadingEmissionDetail = false;
      },
      error: () => {
        if (
          requestSeq !== this.emissionDetailSeq ||
          this.selectedImo !== normalizedImo
        ) {
          return;
        }
        this.emissionDetail = this.applySelectedVesselIdentity(fallback);
        this.emissionDetailSections = buildEmissionDetailSections(
          this.emissionDetail,
        );
        this.isLoadingEmissionDetail = false;
      },
    });
  }

  saveCargoHeatingSection(payload: OnboardingSectionSavePayload): void {
    if (!this.cargoHeatingDetail) {
      return;
    }
    this.savingCargoSectionId = payload.sectionId;
    const previous = this.cargoHeatingDetail;
    const updated = applyOnboardingSectionSave(previous, payload);
    const submitPayload = buildCargoHeatingDetailApiConfig(updated, previous);
    this.cargoHeatingDetail = this.applyMergedOnboardingModules(
      normalizeImoDigits(this.selectedImo),
      updated,
      [],
      this.isCargoHeatingDetailView ? ['cargo-heating'] : [],
    );

    this.chService.postCargoOnboardingSaveConfig({ json: submitPayload }).subscribe({
      next: () => {
        this.cargoHeatingDetailSections = buildCargoHeatingDetailSections(
          this.cargoHeatingDetail!,
        );
        this.savingCargoSectionId = null;
        this.toastr.success('Cargo heating section saved.');
      },
      error: () => {
        this.savingCargoSectionId = null;
        this.toastr.error('Failed to save cargo heating section.');
      },
    });
  }

  saveEmissionSection(payload: OnboardingSectionSavePayload): void {
    if (!this.emissionDetail) {
      return;
    }
    this.savingEmissionSectionId = payload.sectionId;
    const previous = this.emissionDetail;
    const updated = applyOnboardingSectionSave(previous, payload);
    const submitPayload = buildEmissionDetailApiConfig(updated, previous);
    this.emissionDetail = updated;

    this.emissionService.postEmissionVesselOnboardingRegistration(submitPayload).subscribe({
      next: () => {
        this.emissionDetailSections = buildEmissionDetailSections(updated);
        this.savingEmissionSectionId = null;
        this.toastr.success('Emission section saved.');
      },
      error: () => {
        this.savingEmissionSectionId = null;
        this.toastr.error('Failed to save emission section.');
      },
    });
  }

  isObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  resetFilter() {
    this.clearVesselDetailState();
    this.viewMode = 'list';
    this.selectedImo = null;
    this.vesselName = '';
    this.selectedCargoHeatingVessel = null;
    this.selectedEmissionVessel = null;
    this.loadModuleOnboardedVessels();
  }

  openCargoHeatingDetail(vessel: Record<string, unknown>): void {
    const imo = pickImo(vessel);
    if (!imo || imo === '—') {
      return;
    }
    this.viewMode = 'cargo_heating';
    this.selectedCargoHeatingVessel = vessel;
    this.isLoadingChDetail = true;
    this.cargoHeatingDetail = null;
    this.cargoHeatingDetailSections = [];

    const fallback = this.buildCargoHeatingFallback(imo, pickVesselName(vessel));
    const numericImo = Number(normalizeImoDigits(imo));

    forkJoin({
      cargo: this.chService.getCargoHeatingOnboardingData(imo).pipe(
        catchError(() => of(null)),
      ),
      mainConfig:
        Number.isFinite(numericImo) && numericImo > 0
          ? this.requestFormService
              .getOnboardingVesselDetails(numericImo)
              .pipe(catchError(() => of(null)))
          : of(null),
    }).subscribe({
      next: ({ cargo, mainConfig }) => {
        let detail = mergeCargoHeatingDetailSources({
          expectedImo: imo,
          fallback,
          cargoApi: cargo,
          mainVesselDetails: mainConfig,
          listVessel: vessel,
        });
        detail = this.applyMergedOnboardingModules(imo, detail, [], ['cargo-heating']);
        this.cargoHeatingDetail = detail;
        this.cargoHeatingDetailSections = buildCargoHeatingDetailSections(detail);
        this.isLoadingChDetail = false;
      },
      error: () => {
        let detail = mergeCargoHeatingDetailSources({
          expectedImo: imo,
          fallback: {
            ...vessel,
            vessel_name: pickVesselName(vessel),
            imo,
            vessel_imo: imo,
          },
          listVessel: vessel,
        });
        detail = this.applyMergedOnboardingModules(imo, detail, [], ['cargo-heating']);
        this.cargoHeatingDetail = detail;
        this.cargoHeatingDetailSections = buildCargoHeatingDetailSections(detail);
        this.isLoadingChDetail = false;
      },
    });
  }

  openEmissionDetail(vessel: Record<string, unknown>): void {
    const mappedVessel = mapEmissionApiRow(vessel);
    const imo = pickImo(mappedVessel);
    if (!imo || imo === '—') {
      return;
    }
    this.viewMode = 'emission';
    this.selectedEmissionVessel = mappedVessel;
    this.isLoadingEmissionDetail = true;
    this.emissionDetail = null;
    this.emissionDetailSections = [];

    this.emissionService.getEmissionVesselOnboardingDetails(imo).subscribe({
      next: (data) => {
        const detail = data ?? mappedVessel;
        this.emissionDetail = normalizeEmissionDetailRow(detail);
        this.emissionDetailSections = buildEmissionDetailSections(this.emissionDetail);
        this.isLoadingEmissionDetail = false;
      },
      error: () => {
        this.emissionDetail = mappedVessel;
        this.emissionDetailSections = buildEmissionDetailSections(mappedVessel);
        this.isLoadingEmissionDetail = false;
      },
    });
  }

  editCargoHeatingConfiguration(): void {
    const vessel = this.selectedCargoHeatingVessel ?? this.cargoHeatingDetail;
    if (!vessel) {
      return;
    }
    this.chService.setOnboardingVesselContext({
      vessel_name: pickVesselName(vessel),
      imo: pickImo(vessel),
      segment: pickField(vessel, 'segment', 'vessel_segment'),
      vessel_type: pickField(vessel, 'vessel_type', 'vesselType', 'type_of_vessel'),
      client_id: vessel['client_id'],
    });
    this.router.navigate(['/ch-optimization']);
  }

  editEmissionConfiguration(): void {
    const vessel = this.selectedEmissionVessel ?? this.emissionDetail;
    if (!vessel) {
      return;
    }
    this.emissionService.setOnboardingVesselContext({
      vessel_name: pickVesselName(vessel),
      imo: pickImo(vessel),
      segment: pickField(vessel, 'segment'),
      vessel_type: pickField(vessel, 'vessel_type', 'type_of_ship'),
    });
    this.router.navigate(['/vp-onboardingForm']);
  }

  goBack(): void {
    this.router.navigate(['/vp-onboardingForm']);
  }

  getVesselCardName(vessel: Record<string, unknown>): string {
    return pickVesselName(vessel);
  }

  getVesselCardImo(vessel: Record<string, unknown>): string {
    return pickImo(vessel);
  }

  getVesselCardClient(vessel: Record<string, unknown>): string {
    const client = pickField(vessel, 'client', 'client_name');
    if (client !== '—') {
      return client;
    }
    const clientId = vessel['client_id'];
    if (clientId != null && clientId !== '') {
      return `Client #${clientId}`;
    }
    return '—';
  }

  getVesselCardSegment(vessel: Record<string, unknown>): string {
    return pickField(vessel, 'segment', 'vessel_segment');
  }

  getVesselCardStatus(vessel: Record<string, unknown>): string {
    return formatOnboardingStatus(
      pickField(vessel, 'onboarding_status', 'status'),
    );
  }

  getVesselCardUpdated(vessel: Record<string, unknown>): string {
    return formatLastUpdated(vessel);
  }

  getEmissionType(vessel: Record<string, unknown>): string {
    return pickField(vessel, 'emission_type', 'type_of_ship', 'vessel_type');
  }

  getSteamParamHeaders(paramData: any): string[] {
    if (Array.isArray(paramData) && paramData.length > 0) {
      return Object.values(paramData[0]);
    }
    return [];
  }

  getSteamParamRows(paramData: any): any[] {
    if (Array.isArray(paramData) && paramData.length > 1) {
      return paramData.slice(1).map((row) => Object.values(row));
    }
    return [];
  }
  getObjectKeys(obj: any): string[] {
    if (!obj) return [];
    return Object.keys(obj).sort((a, b) => parseFloat(a) - parseFloat(b));
  }

  hasMeaningfulCpLimits(res: any): boolean {
    if (!res || typeof res !== 'object') return false;
    const hasValue = (val: any) =>
      val !== null && val !== undefined && val !== '' && val !== 0 && val !== '0';
    return (
      hasValue(res.wind_bf) ||
      hasValue(res.sig_wave) ||
      hasValue(res.swell_height) ||
      hasValue(res.wind_wave) ||
      (Boolean(res.about_sc) && res.about_sc !== '') ||
      (Boolean(res.current_factor) && res.current_factor !== '')
    );
  }

  applyCpLimitsFromConfig(config: any, clientName: string): void {
    if (!config) {
      this.setDefaultCpLimits(clientName);
      return;
    }
    const source =
      (config as any)?.onboarding_data || (config as any)?.data || config;
    const form = source?.onboarding_form || {};
    const vesselObj = source?.vessel || {};
    const realClient =
      form.clientName || vesselObj.client_name || source.clientName || clientName;

    const parseNum = (val: any, defaultVal: number): number => {
      if (val === null || val === undefined || val === '') return defaultVal;
      const parsed = parseFloat(val);
      return isNaN(parsed) ? defaultVal : parsed;
    };

    const normalizeYesNo = (val: any, defaultVal: string): string => {
      if (!val) return defaultVal;
      const s = val.toString().trim().toLowerCase();
      if (s === 'yes' || s === 'y' || s === 'true') return 'Yes';
      if (s === 'no' || s === 'n' || s === 'false') return 'No';
      return defaultVal;
    };

    const normalizeAdverse = (val: any): string => {
      if (!val) return 'Not Included';
      const s = val.toString().trim().toLowerCase();
      if (s.includes('inc') && !s.includes('not')) return 'Included';
      return 'Not Included';
    };

    if (realClient) {
      this.clientName = realClient;
    }

    this.cpLimits = {
      wind_bf: parseNum(form.windBeaufort ?? form.wind_bf, 4),
      sig_wave: parseNum(form.significantWave ?? form.sig_wave, 1.25),
      adverse_current: normalizeAdverse(form.adverseCurrent ?? form.adverse_current),
      swell_height: parseNum(form.swellHeight ?? form.swell_height, 1.25),
      wind_wave: parseNum(form.windWave ?? form.wind_wave, 1.25),
      about_sc: normalizeYesNo(form.aboutSc ?? form.about_sc, 'Yes'),
      current_factor: normalizeYesNo(form.currentFactor ?? form.current_factor, 'No'),
      bad_weather_hours: parseNum(form.goodWeatherPeriod ?? form.bad_weather_hours, 12),
      client_name: this.clientName,
    };
    this.cpLimitsForm.patchValue(this.cpLimits);
  }

  fetchCpLimits(imo: any, clientName: string, fallbackConfig?: any) {
    this.requestFormService.getVesselCpLimits(imo, clientName).subscribe({
      next: (res: any) => {
        if (this.hasMeaningfulCpLimits(res)) {
          this.cpLimits = res;
          this.clientName = res.client_name || clientName;
          this.cpLimitsForm.patchValue({
            wind_bf: res.wind_bf,
            sig_wave: res.sig_wave,
            adverse_current: res.adverse_current,
            swell_height: res.swell_height,
            wind_wave: res.wind_wave,
            about_sc: res.about_sc,
            current_factor: res.current_factor,
            bad_weather_hours: res.bad_weather_hours,
          });
        } else if (fallbackConfig || this.latestMainConfig) {
          this.applyCpLimitsFromConfig(fallbackConfig || this.latestMainConfig, clientName);
        } else {
          this.setDefaultCpLimits(clientName);
        }
      },
      error: () => {
        if (fallbackConfig || this.latestMainConfig) {
          this.applyCpLimitsFromConfig(fallbackConfig || this.latestMainConfig, clientName);
        } else {
          this.setDefaultCpLimits(clientName);
        }
      },
    });
  }

  private setDefaultCpLimits(clientName: string) {
    this.clientName = clientName;
    this.cpLimits = {
      wind_bf: 4,
      sig_wave: 1.25,
      adverse_current: 'Not Included',
      swell_height: 2.0,
      wind_wave: 1.25,
      about_sc: 'Yes',
      current_factor: 'No',
      bad_weather_hours: 12,
      client_name: clientName
    };
    this.cpLimitsForm.patchValue(this.cpLimits);
  }

  saveLimits() {
    if (this.cpLimitsForm.invalid || !this.selectedImo || !this.clientName) {
      return;
    }
    const payload = {
      imo: this.selectedImo,
      client_name: this.clientName,
      ...this.cpLimitsForm.value
    };
    this.requestFormService.saveVesselCpLimits(payload).subscribe({
      next: () => {
        this.isEditingLimits = false;
        this.toastr.success('Charter Party limits saved successfully');
        this.fetchCpLimits(this.selectedImo, this.clientName);
      },
      error: (err) => {
        console.error('Failed to save limits', err);
      }
    });
  }

  toggleManageLogs(index: number) {
    if (this.activeLogEditCardIndex === index) {
      this.activeLogEditCardIndex = -1;
    } else {
      this.activeLogEditCardIndex = index;
      this.newSpeedVal = '';
      this.newConsVal = '';
      this.newSpeedError = '';
      const speedConsObj = this.vesselTcpSpeedConsumption[index].speed_consumption || {};
      this.localSpeedConsArray = Object.keys(speedConsObj).map(key => ({
        speed: key,
        cons: speedConsObj[key]?.toString() || ''
      })).sort((a, b) => parseFloat(a.speed) - parseFloat(b.speed));
    }
  }

  localSaveLogPoint(speed: string, cons: string) {
    if (!speed || !cons) return;
    const speedNum = parseFloat(speed);
    const consNum = parseFloat(cons);
    if (isNaN(speedNum) || isNaN(consNum)) {
      this.newSpeedError = 'Speed and Consumption must be numeric';
      return;
    }
    this.localSpeedConsArray.push({
      speed: speedNum.toString(),
      cons: consNum.toString()
    });
    this.localSpeedConsArray.sort((a, b) => parseFloat(a.speed) - parseFloat(b.speed));
    this.newSpeedVal = '';
    this.newConsVal = '';
    this.newSpeedError = '';
  }

  localDeleteLogPoint(index: number) {
    this.localSpeedConsArray.splice(index, 1);
  }

  saveAllLogPoints(item: any) {
    if (!this.selectedImo) return;
    
    // Auto-save the input fields if they are filled when saving all
    if (this.newSpeedVal && this.newConsVal) {
      const speedNum = parseFloat(this.newSpeedVal);
      const consNum = parseFloat(this.newConsVal);
      if (!isNaN(speedNum) && !isNaN(consNum)) {
        this.localSpeedConsArray.push({
          speed: speedNum.toString(),
          cons: consNum.toString()
        });
      }
      this.newSpeedVal = '';
      this.newConsVal = '';
    }

    const speedConsumptionObj: Record<string, string> = {};
    for (const pt of this.localSpeedConsArray) {
      if (pt.speed && pt.cons) {
        speedConsumptionObj[pt.speed] = pt.cons;
      }
    }
    this.localSpeedCons = speedConsumptionObj;

    const payload = {
      imo: Number(this.selectedImo),
      imo_number: Number(this.selectedImo),
      client: item.client,
      condition: item.condition,
      segment: item.segment,
      ship_name: item.ship_name,
      speed_consumption: this.localSpeedCons
    };

    this.requestFormService.updateVesselTcpSpeedConsumptionBulk(payload).subscribe({
      next: (res: any) => {
        item.speed_consumption = { ...this.localSpeedCons };
        this.activeLogEditCardIndex = -1; // close the pane
        this.toastr.success('Speed & Consumption points saved successfully');
      },
      error: (err) => {
        console.error('Failed to bulk save speed points', err);
        // Fallback to sequential updates if bulk endpoint is not available
        this.fallbackSequentialUpdate(item);
      }
    });
  }

  async fallbackSequentialUpdate(item: any) {
    const keys = Object.keys(this.localSpeedCons);
    // First, clear everything not in localSpeedCons
    const oldKeys = Object.keys(item.speed_consumption || {});
    for (const oldKey of oldKeys) {
      if (!(oldKey in this.localSpeedCons)) {
        await this.requestFormService.updateVesselTcpSpeedConsumptionPoint({
          imo: Number(this.selectedImo), imo_number: Number(this.selectedImo),
          client: item.client, condition: item.condition,
          segment: item.segment, ship_name: item.ship_name,
          speed: oldKey, consumption: '0', action: 'delete'
        }).toPromise();
      }
    }
    // Then save all current keys
    for (const key of keys) {
      await this.requestFormService.updateVesselTcpSpeedConsumptionPoint({
        imo: Number(this.selectedImo), imo_number: Number(this.selectedImo),
        client: item.client, condition: item.condition,
        segment: item.segment, ship_name: item.ship_name,
        speed: key, consumption: this.localSpeedCons[key], action: 'save'
      }).toPromise();
    }
    item.speed_consumption = { ...this.localSpeedCons };
    this.activeLogEditCardIndex = -1;
  }

  saveLogPoint(item: any, speed: string, cons: string) {
    if (!speed || !cons || !this.selectedImo) return;
    
    const speedNum = parseFloat(speed);
    const consNum = parseFloat(cons);
    if (isNaN(speedNum) || isNaN(consNum)) {
      this.newSpeedError = 'Speed and Consumption must be numeric';
      return;
    }

    const payload = {
      imo: Number(this.selectedImo),
      imo_number: Number(this.selectedImo),
      client: item.client,
      condition: item.condition,
      segment: item.segment,
      ship_name: item.ship_name,
      speed: speedNum.toString(),
      consumption: consNum.toString(),
      action: 'save'
    };

    this.requestFormService.updateVesselTcpSpeedConsumptionPoint(payload).subscribe({
      next: (res: any) => {
        item.speed_consumption = res.speed_consumption;
        this.newSpeedVal = '';
        this.newConsVal = '';
        this.newSpeedError = '';
      },
      error: (err) => {
        console.error('Failed to save speed point', err);
      }
    });
  }

  editLogPoint(item: any, oldSpeed: string, newSpeed: string, newCons: string) {
    if (!newSpeed || !newCons || !this.selectedImo) return;
    
    const speedNum = parseFloat(newSpeed);
    const consNum = parseFloat(newCons);
    if (isNaN(speedNum) || isNaN(consNum)) {
      this.newSpeedError = 'Speed and Consumption must be numeric';
      return;
    }

    if (oldSpeed !== newSpeed) {
      const deletePayload = {
        imo: Number(this.selectedImo),
        imo_number: Number(this.selectedImo),
        client: item.client,
        condition: item.condition,
        segment: item.segment,
        ship_name: item.ship_name,
        speed: oldSpeed,
        consumption: '0',
        action: 'delete'
      };
      
      this.requestFormService.updateVesselTcpSpeedConsumptionPoint(deletePayload).subscribe({
        next: () => {
          this.saveLogPoint(item, newSpeed, newCons);
        },
        error: (err) => console.error('Failed to delete old point on edit', err)
      });
    } else {
      this.saveLogPoint(item, newSpeed, newCons);
    }
  }

  deleteLogPoint(item: any, speed: string) {
    if (!speed || !this.selectedImo) return;

    const payload = {
      imo: Number(this.selectedImo),
      imo_number: Number(this.selectedImo),
      client: item.client,
      condition: item.condition,
      segment: item.segment,
      ship_name: item.ship_name,
      speed: speed,
      consumption: '0',
      action: 'delete'
    };

    this.requestFormService.updateVesselTcpSpeedConsumptionPoint(payload).subscribe({
      next: (res: any) => {
        item.speed_consumption = res.speed_consumption;
      },
      error: (err) => {
        console.error('Failed to delete speed point', err);
      }
    });
  }

  formatLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatBoilerCapacityKgHr(value: unknown): string {
    if (value == null || value === '') {
      return '—';
    }
    const num = Number(String(value).replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(num) || num <= 0) {
      return String(value);
    }
    const kgHr = num < 500 ? Math.round(num * 1000) : Math.round(num);
    return `${kgHr.toLocaleString()} kg/hr`;
  }
}
